import React, { useCallback, useMemo } from 'react'
import { Form, Formik, FormikProps } from 'formik'
import { OffersWithErrors } from '../../../modules/privateOffer/privateOfferCreation/reducer'
import { ButtonV2, LabraToolTipV2, useAuth } from '@labrav/react-components'
import { useSelector } from '../../../store/hooks'
import {
  CloudMarketplace,
  Product,
} from '../../../modules/productsListing/reducer'
import { FooterV2 } from '../../../common/components/FooterV2/FooterV2'
import { useStyles } from './PrivateOfferContainerAddEditFormik.styles'
import { get, isEmpty } from 'lodash'
import { DateTime } from 'luxon'
import { ClassNameMap } from '@mui/styles'
import {
  generateValidationSchema,
  getMicrosoftPrivateOfferValidationSchema,
  isAzureContainerProduct,
  isAzureVmProduct,
  MemoizedPrivateOfferAddEditContainer,
  type Product as MarketplaceProduct,
  normalizeVariableStartDate,
  resolvePurchaseTermsCustomized,
  getContractDurationMode,
} from '@labrav/marketplace'
import { AzureOfferPlan } from '../../../modules/privateOffer/reducer'
import { shallowEqual } from 'react-redux'
import { providerToAuthCloud } from '@labrav/common'
import {
  LoadingTypes,
  isLoading,
  useGetAllUsers,
  useUserType,
} from '@labrav/common'
import { usePrivateOfferCreationTracking } from '../../../common/utils/hooks/usePrivateOfferCreationTracking'
const hasAtLeastOneSelectedAzurePlan = (plans?: AzureOfferPlan[]) =>
  (plans || []).some(plan => !isEmpty(plan?.labraPlanId))

const hasInvalidAzureOfferDateRange = ({
  offerStartDate,
  offerExpirationDate,
  variableStartDate,
}: {
  offerStartDate?: string | null
  offerExpirationDate?: string | null
  variableStartDate?: boolean | string | null
}) => {
  const isSpecifiedMonth =
    variableStartDate === false || variableStartDate === 'false'

  if (!isSpecifiedMonth || !offerStartDate || !offerExpirationDate) {
    return false
  }

  const start = DateTime.fromISO(String(offerStartDate))
  const end = DateTime.fromISO(String(offerExpirationDate))

  return start.isValid && end.isValid && end < start
}

const shouldDisableAzureContainerSaveAsDraft = (plans?: AzureOfferPlan[]) =>
  !hasAtLeastOneSelectedAzurePlan(plans)

export type PrivateOfferContainerAddEditFormikProps = {
  product: Product
  footerWidth?: string
  conditionalClassNamesForHubspot?: ClassNameMap
  offset: number
  savePrivateOffer: (
    saveAsDraft: boolean,
    formikOffer: OffersWithErrors
  ) => void
  hideStakeholders?: boolean
  tabTop?: string
  cloud?: CloudMarketplace
  isSalesforceContext?: boolean
}

type PrivateOfferFormikBodyProps = {
  formikProps: FormikProps<OffersWithErrors>
  product: Product
  footerWidth?: string
  conditionalClassNamesForHubspot?: ClassNameMap
  offset: number
  hideStakeholders?: boolean
  tabTop?: string
  cloud?: CloudMarketplace
  classes: ReturnType<typeof useStyles>['classes']
  loading: boolean
  offerSubmitloading: boolean
  savePrivateOffer: (
    saveAsDraft: boolean,
    formikOffer: OffersWithErrors
  ) => void
  isOfferSaveAsDraftDisabled: (
    formikProps: FormikProps<OffersWithErrors>
  ) => boolean | undefined
  isSalesforceContext?: boolean
}

/** Renders Form + footer; must be a real component so hooks are not used inside Formik’s render-prop function. */
const PrivateOfferFormikBody: React.FC<PrivateOfferFormikBodyProps> = ({
  formikProps,
  product,
  footerWidth,
  conditionalClassNamesForHubspot,
  offset,
  hideStakeholders,
  tabTop,
  cloud,
  classes,
  loading,
  offerSubmitloading,
  savePrivateOffer,
  isOfferSaveAsDraftDisabled,
  isSalesforceContext = false,
}) => {
  const { loading: authLoading, canDo } = useAuth()
  const partnerType = useUserType()
  const { users } = useGetAllUsers(partnerType)

  const canEditDraft = useMemo(() => {
    if (!cloud) return false
    return (
      !authLoading &&
      canDo({
        cloud: providerToAuthCloud(cloud),
        service: 'marketplace',
        action: 'create',
      })
    )
  }, [authLoading, canDo, cloud])
  const permissionMessageString =
    'This action is disabled because you have read-only access'

  const { handleSubmitPrivateOffer } = usePrivateOfferCreationTracking({
    cloud,
    productId: product.productId,
    formikProps,
    canSubmit: canEditDraft,
  })

  const handleSaveAsDraft = useCallback(async () => {
    savePrivateOffer(true, formikProps.values)
  }, [formikProps.values, savePrivateOffer])

  return (
    <Form>
      <div className={classes.root} data-testid="formik-Container">
        <MemoizedPrivateOfferAddEditContainer
          product={product as MarketplaceProduct}
          conditionalClassNamesForHubspot={conditionalClassNamesForHubspot}
          offset={offset}
          hideStakeholders={hideStakeholders}
          tabTop={tabTop}
          cloud={cloud}
          isSalesforceContext={isSalesforceContext}
          stakeholderUsers={users}
        />
      </div>
      <FooterV2
        actions={
          <div className={classes.footerWrapper}>
            {!canEditDraft ? (
              <LabraToolTipV2
                tooltipPlacement="bottom"
                description={permissionMessageString}
              >
                <span>
                  <ButtonV2
                    styleType="outlined"
                    onClick={handleSaveAsDraft}
                    data-testid="save-as-draft"
                    loading={loading}
                    disabled
                  >
                    Save as draft
                  </ButtonV2>
                </span>
              </LabraToolTipV2>
            ) : (
              <ButtonV2
                styleType="outlined"
                onClick={handleSaveAsDraft}
                data-testid="save-as-draft"
                loading={loading}
                disabled={isOfferSaveAsDraftDisabled(formikProps)}
              >
                Save as draft
              </ButtonV2>
            )}
            {!canEditDraft ? (
              <LabraToolTipV2
                tooltipPlacement="bottom"
                description={permissionMessageString}
              >
                <span>
                  <ButtonV2
                    data-testid="save-button-private-offer"
                    styleType="primary"
                    onClick={handleSubmitPrivateOffer}
                    loading={offerSubmitloading}
                    disabled={!canEditDraft}
                  >
                    Send to buyer
                  </ButtonV2>
                </span>
              </LabraToolTipV2>
            ) : (
              <ButtonV2
                data-testid="save-button-private-offer"
                styleType="primary"
                onClick={handleSubmitPrivateOffer}
                loading={offerSubmitloading}
              >
                Send to buyer
              </ButtonV2>
            )}
          </div>
        }
        width={footerWidth}
        customClass={classes.customFooter}
      />
    </Form>
  )
}

export const PrivateOfferContainerAddEditFormik: React.FC<
  PrivateOfferContainerAddEditFormikProps
> = ({
  product,
  footerWidth,
  conditionalClassNamesForHubspot,
  offset,
  savePrivateOffer,
  hideStakeholders,
  tabTop,
  cloud,
  isSalesforceContext: isSalesforceContextProp,
}) => {
  const { classes } = useStyles()

  const isSalesforceContext = isSalesforceContextProp ?? false

  const privateOffer = useSelector(
    state =>
      state.privateOffersInCreation.currentPrivateOfferInCreation[
        product?.productId
      ],
    shallowEqual
  )

  const offerSubmitloading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.SUBMIT_FLYOUT_Offer)
  )
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )

  const isOfferSaveAsDraftDisabled = useCallback(
    (formikProps: FormikProps<OffersWithErrors>): boolean | undefined => {
      const { errors, touched } = formikProps
      if (cloud === 'AWS') {
        const dimensionsErr = touched?.dimensions && errors?.dimensions
        return dimensionsErr ? true : undefined
      }
      if (cloud === 'AZURE') {
        const {
          offerStartDate,
          offerExpirationDate,
          variableStartDate,
          plans,
        } = formikProps.values

        if (
          hasInvalidAzureOfferDateRange({
            offerStartDate,
            offerExpirationDate,
            variableStartDate,
          })
        ) {
          return true
        }

        if (isAzureContainerProduct(product)) {
          return shouldDisableAzureContainerSaveAsDraft(plans) || undefined
        }

        if (isAzureVmProduct(product)) {
          return shouldDisableAzureContainerSaveAsDraft(plans) || undefined
        }

        const hasPlansError = !isEmpty(
          get((formikProps.errors as Record<string, unknown>) || {}, 'plans')
        )

        return hasPlansError || undefined
      }
      return undefined
    },
    [cloud, product]
  )

  const productDimensions = useMemo(() => {
    return (
      product?.productDimensions?.filter(
        dimension => dimension.isAdditionalUsageAllowed === false
      ) || []
    )
  }, [product])
  const dimensionsOptions = useMemo(() => {
    return productDimensions.map(dimension => dimension.label)
  }, [productDimensions])

  const privateOfferFormikValidationSchema = useMemo(
    () =>
      cloud === 'AWS'
        ? generateValidationSchema(
            product.productType,
            product.containerPricingModel,
            hideStakeholders,
            dimensionsOptions
          )
        : getMicrosoftPrivateOfferValidationSchema(
            isSalesforceContext,
            product
          ),
    [
      cloud,
      product.productType,
      product.containerPricingModel,
      hideStakeholders,
      dimensionsOptions,
      isSalesforceContext,
    ]
  )

  const formInitialValues = useMemo((): OffersWithErrors => {
    const contractDurationMode = getContractDurationMode(privateOffer)

    if (cloud !== 'AZURE') {
      return {
        ...privateOffer,
        contractDurationMode,
      } as OffersWithErrors
    }

    const variableStartDate = normalizeVariableStartDate(
      privateOffer?.variableStartDate
    )

    return {
      ...privateOffer,
      variableStartDate,
      contractDurationMode,
      purchaseTermsCustomized: resolvePurchaseTermsCustomized({
        variableStartDate,
        offerStartDate: privateOffer?.offerStartDate,
        offerExpirationDate: privateOffer?.offerExpirationDate,
        createdAt: privateOffer?.createdAt,
        metaData: privateOffer?.metaData,
      }),
    } as OffersWithErrors
  }, [cloud, privateOffer])

  return (
    <>
      {!isEmpty(privateOffer) && (
        <Formik
          initialValues={formInitialValues}
          validationSchema={privateOfferFormikValidationSchema}
          validateOnMount={true}
          validateOnBlur={true}
          validateOnChange={false}
          onSubmit={values => {
            savePrivateOffer(false, values)
          }}
          enableReinitialize
        >
          {formikProps => (
            <PrivateOfferFormikBody
              formikProps={formikProps}
              product={product}
              footerWidth={footerWidth}
              conditionalClassNamesForHubspot={conditionalClassNamesForHubspot}
              offset={offset}
              hideStakeholders={hideStakeholders}
              tabTop={tabTop}
              cloud={cloud}
              classes={classes}
              loading={loading}
              offerSubmitloading={offerSubmitloading}
              savePrivateOffer={savePrivateOffer}
              isOfferSaveAsDraftDisabled={isOfferSaveAsDraftDisabled}
              isSalesforceContext={isSalesforceContext}
            />
          )}
        </Formik>
      )}
    </>
  )
}
